import React, { PropsWithChildren, useMemo } from 'react';

import { Patient } from 'src/interfaces';
import { FolderPlus, Info } from 'react-native-feather';
import InfoCard from '../../PatientProfile/components/ViewProfile/InfoCard';
import {
  basicInformation,
  additionalInformation
} from '../../PatientProfile/components/ViewProfile/helpers';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import Header from '../../PatientProfile/components/ViewProfile/Header';
import styled from 'styled-components/native';
import { Margin } from 'src/constants';
import { useTranslation } from 'react-i18next';
import { palette } from 'src/theme';
import { transparent } from 'src/utils';
import { SIDEBAR_WIDTH } from 'src/module/FormKit/components/constants';
import PatientProfileAvatar from 'src/scenes/PatientProfileUpdate/components/PatientProfileAvatar';
import { ReducerState } from 'src/scenes/PatientProfileUpdate/model';
import { FormStateHook } from 'src/scenes/PatientProfileUpdate/hooks/useFormState';

interface Props {
  patient: Patient;
  showEditButton?: boolean;
  state: ReducerState;
  newPatientImageUri: string | null;
  patientIsMutating: boolean;
  setFieldValue: FormStateHook['setFieldValue'];
  values: Patient | any;
}
const EditPatientProfileSidebar: React.FC<Props> = ({
  patient,
  showEditButton,
  state,
  newPatientImageUri,
  patientIsMutating,
  setFieldValue,
  values
}) => {
  const { t } = useTranslation('patientProfile');
  const { colors, viewMode } = useAppTheme();

  const patientInfo = useMemo(() => {
    const basicInfo = basicInformation(values);
    const additionalInfo = additionalInformation(values);
    return { basicInfo, additionalInfo };
  }, [values]);

  const Container = (
    viewMode.isWeb ? SidebarScrollView : SidebarView
  ) as React.FC<PropsWithChildren>;

  return (
    <Container>
      <PatientProfileAvatar
        state={state}
        patient={patient}
        newPatientImageUri={newPatientImageUri}
        setFieldValue={setFieldValue}
        patientIsMutating={patientIsMutating}
      />
      <Header patient={values} />
      <Divider />
      <InfoCard
        title={t('basicInfo')}
        icon={() => <Info color={colors.onSurface} />}
        items={patientInfo.basicInfo}
      />
      <Divider />
      <InfoCard
        title={t('additionalInfo')}
        icon={() => <FolderPlus color={colors.onSurface} />}
        items={patientInfo.additionalInfo}
      />
    </Container>
  );
};

export default EditPatientProfileSidebar;

const SidebarView = styled.View`
  height: 100%;
  border-bottom-left-radius: ${Margin.Large};
  border-bottom-right-radius: ${Margin.Large};
  background-color: ${transparent(palette.SELECTOR_GREY, 0.5)};
  gap: ${Margin.ExtraLarge}px;
  padding-bottom: ${Margin.ExtraLarge}px;
  margin-horizontal: ${({ theme }) => (theme.viewMode.isTablet ? Margin.ExtraLarge : 0)}px;
  flex-grow: 0;
`;

const SidebarScrollView = styled.ScrollView.attrs({
  contentContainerStyle: {
    width: SIDEBAR_WIDTH,
    gap: Margin.ExtraLarge,
    paddingBottom: Margin.ExtraLarge
  }
})`
  flex-grow: 0;
  background-color: ${transparent(palette.SELECTOR_GREY, 0.5)};
`;

const Divider = styled.View`
  border-bottom-width: 0.5px;
  border-bottom-color: ${({ theme }) => theme.colors.disabled};
  margin-horizontal: ${Margin.Large}px;
`;
